import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, List, ListItemButton,
  ListItemText, Paper, Stack, Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import healthService, { UnlinkedWineItemsResponse } from '../../../services/healthService';

/**
 * SIMPLE-WINE.4 §6 — UNLINKED WINE ITEMS.
 *
 * A standing report, deliberately not a review-queue task type. The usual way
 * an item ends up here is a rejected provisional: the venue created it against
 * a wine a reviewer then threw out, and the item stayed behind with a name
 * that traces to nothing.
 *
 * Making these queue tasks would put rejected entries back in front of a
 * reviewer as work — the precise thing rejecting them was meant to stop. A
 * report is consulted; a queue asks. These want the first.
 *
 * Grouped by venue because that is who can fix them: the person who knows
 * which bottle is actually on the shelf is at the venue.
 */

const UnlinkedWineItems: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<UnlinkedWineItemsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await healthService.getUnlinkedWineItems());
    } catch {
      setError('Could not load the report. Try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const size = (v: number | null, u: string | null) => (v && u ? `${v} ${u}` : null);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Unlinked wine items
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active WINE items with no link to the wine catalog. Usually left
            behind when the wine they were created against was rejected. Open an
            item to link it to a catalog wine — its name updates to the catalog
            name when you do.
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchReport}
          disabled={loading}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !data || data.total === 0 ? (
        <Alert severity="success">
          Nothing unlinked. Every active wine item points at a catalog wine.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {data.total} item{data.total === 1 ? '' : 's'} across{' '}
            {data.companies.length} compan{data.companies.length === 1 ? 'y' : 'ies'}.
          </Typography>

          <Stack spacing={2}>
            {data.companies.map((company) => (
              <Paper
                key={company.companyId}
                elevation={0}
                sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {/* A company whose venues the cache could not resolve still
                        gets its row — the id is enough to act on. */}
                    {company.companyName ?? `Company ${company.companyId}`}
                  </Typography>
                  <Chip size="small" label={`${company.count} unlinked`} color="warning" variant="outlined" />
                </Stack>

                <List dense disablePadding>
                  {company.items.map((item, i) => (
                    <React.Fragment key={item._id}>
                      {i > 0 && <Divider component="li" />}
                      {/* GOVERNANCE-LINK.1 — the detail page is venue-scoped, so a
                          row is only openable when the API resolved a venue to go
                          through. Without one it stays a readable row rather than
                          a button that navigates to /admin/venues/undefined/… */}
                      <ListItemButton
                        disabled={!company.linkVenueId}
                        onClick={() => company.linkVenueId && navigate(
                          `/admin/venues/${company.linkVenueId}/inventory/${item._id}`,
                        )}
                      >
                        <ListItemText
                          primary={item.displayName}
                          secondary={size(item.sizeValue, item.sizeUnit) ?? undefined}
                        />
                      </ListItemButton>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default UnlinkedWineItems;
